import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert,
  CircularProgress,
  Autocomplete
} from "@mui/material";
import {
  Edit,
  ArrowForward,
  Close
} from "@mui/icons-material";
import { systemPromptService } from "../../services/systemPromptService";
import { CHAT_CATEGORY_OPTIONS } from "../../constants/apiConstants";
import { getPageNumbers } from "../../utils/pagination";

// Custom Input Field Component
const CustomInputField = ({
  label,
  placeholder,
  value,
  onChange,
  multiline = false,
  rows = 1,
  fullWidth = true,
  width = "100%",
  autoFocus = false,
  ...props
}) => {
  return (
    <TextField
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      multiline={multiline}
      rows={multiline ? rows : undefined}
      fullWidth={fullWidth}
      autoFocus={autoFocus}
      InputLabelProps={{
        shrink: true,
      }}
      sx={{
        width: fullWidth ? "100%" : width,
        "& .MuiOutlinedInput-root": {
          backgroundColor: "#F5F5F5",
          borderRadius: "12px",
          "& fieldset": {
            borderColor: "#E1AD01",
          },
          "&:hover fieldset": {
            borderColor: "#B8941F",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#E1AD01",
          },
        },
        "& .MuiInputLabel-root": {
          color: "black",
          fontWeight: 600,
          padding: "4px 12px",
          borderRadius: "12px",
          backgroundColor: "rgba(204, 158, 73, 1)",
          transform: "translate(14px, -9px) scale(0.75)",
          "&.Mui-focused": {
            padding: "4px 12px",
            borderRadius: "12px",
            color: "black",
            backgroundColor: "rgba(204, 158, 73, 1)",
            transform: "translate(14px, -9px) scale(0.75)",
          },
          "&.MuiInputLabel-shrink": {
            padding: "4px 12px",
            borderRadius: "12px",
            backgroundColor: "rgba(204, 158, 73, 1)",
            transform: "translate(14px, -9px) scale(0.75)",
          },
        },
        "& .MuiInputBase-input": {
          padding: "14px 16px",
          color: "#000000"
        },
        "& .MuiInputBase-input.Mui-disabled": {
          color: "#000000 !important",
          WebkitTextFillColor: "#000000 !important"
        },
        "& .MuiOutlinedInput-root.Mui-disabled": {
          "& fieldset": {
            borderColor: "#E0E0E0",
          },
        }
      }}
      {...props}
    />
  );
};

const ChatAI = () => {
  // const [chatForm, setChatForm] = useState({
  //   questionNo: "",
  //   coinsPerQuestion: "",
  //   pricePerCoins: "",
  //   generalizedPrompt: ""
  // });

  // const [categoryForm, setCategoryForm] = useState({
  //   categoryName: "",
  //   categoryPrompt: ""
  // });

  // System prompts state
  const [systemPrompts, setSystemPrompts] = useState([]);
  const [loadingPrompts, setLoadingPrompts] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);

  const [modalOpen, setModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    categoryName: "",
    prompt: ""
  });

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [originalEditForm, setOriginalEditForm] = useState(null);

  // API related states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [neutralMessage, setNeutralMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  

  const fetchSystemPrompts = useCallback(async () => {
    try {
      setLoadingPrompts(true);
      const result = await systemPromptService.getSystemPrompts();

      if (result.success && result.data?.result) {
        setSystemPrompts(result.data.result);
        // Calculate total pages for pagination
        setTotalPages(Math.ceil(result.data.result.length / itemsPerPage));
      } else {
        console.error("Failed to fetch system prompts:", result.message);
      }
    } catch (error) {
      console.error("Error fetching system prompts:", error);
    } finally {
      setLoadingPrompts(false);
    }
  }, [itemsPerPage]);

  // Fetch system prompts on component mount
  useEffect(() => {
    fetchSystemPrompts();
  }, [fetchSystemPrompts]);

  // Get current page items for pagination
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return systemPrompts.slice(startIndex, endIndex);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // const handleChatFormChange = (field, value) => {
  //   setChatForm(prev => ({ ...prev, [field]: value }));
  // };

  // const handleCategoryFormChange = (field, value) => {
  //   setCategoryForm(prev => ({ ...prev, [field]: value }));
  // };

  // const handleSave = () => {
  //   console.log("Saving chat configuration:", chatForm);
  // };

  // const handleReset = () => {
  //   setCategoryForm({
  //     categoryName: "",
  //     categoryPrompt: ""
  //   });
  // };

  // const handleAdd = () => {
  //   console.log("Adding category:", categoryForm);
  // };

  const handleEditCategory = (promptId) => {
    // Find the prompt to edit
    const promptToEdit = systemPrompts.find(prompt =>
      (prompt._id?.$oid || prompt._id) === promptId
    );

    if (promptToEdit) {
      const newEditForm = {
        categoryName: promptToEdit.category,
        prompt: promptToEdit.prompt
      };
      setEditForm(newEditForm);
      setOriginalEditForm(newEditForm);
      setIsEditMode(true);
      setEditingPromptId(promptId);
      setModalOpen(true);
    }
  };

  const handleOpenModal = () => {
    // Reset to add mode
    setIsEditMode(false);
    setEditingPromptId(null);
    setEditForm({
      categoryName: "",
      prompt: ""
    });
    setOriginalEditForm(null);
    setModalOpen(true);
  };



  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    if (neutralMessage) setNeutralMessage("");
  };

  // no word-limit enforcement for prompts

  const handleModalReset = () => {
    if (isEditMode) {
      // In edit mode, close the modal
      handleCloseModal();
    } else {
      // In add mode, reset the form
      setEditForm({
        categoryName: "",
        prompt: ""
      });
      setError("");
      setSuccess("");
      setFieldErrors({});
    }
  };

  const validateModalForm = () => {
    const errors = {};

    // Only validate category if not in edit mode
    if (!isEditMode && !editForm.categoryName) {
      errors.categoryName = "Category is required";
    }

    if (!editForm.prompt) {
      errors.prompt = "Prompt is required";
    } else if (editForm.prompt.length < 10) {
      errors.prompt = "Prompt must be at least 10 characters long";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    return true;
  };

  const handleModalCustomize = async () => {
    setError("");
    setSuccess("");
    setNeutralMessage("");
    setFieldErrors({});

    if (!validateModalForm()) {
      return;
    }

    // If in edit mode and no changes were made, show neutral message and do not proceed
    if (isEditMode) {
      const orig = originalEditForm || { categoryName: "", prompt: "" };
      const trimmedOrigPrompt = (orig.prompt || "").trim();
      const trimmedNewPrompt = (editForm.prompt || "").trim();
      const origCategory = (orig.categoryName || "").trim();
      const newCategory = (editForm.categoryName || "").trim();

      if (trimmedOrigPrompt === trimmedNewPrompt && origCategory === newCategory) {
        setNeutralMessage("No changes to save.");
        return;
      }
    }

    setLoading(true);

    try {
      let result;

      if (isEditMode && editingPromptId) {
        // Update existing prompt (allow updating category too)
        result = await systemPromptService.updateSystemPrompt(editingPromptId, {
          prompt: editForm.prompt,
          category: editForm.categoryName
        });
      } else {
        // Create new prompt
        result = await systemPromptService.createSystemPrompt({
          category: editForm.categoryName,
          prompt: editForm.prompt
        });
      }

      if (result.success) {
        setSuccess(result.message || (isEditMode ? 'System prompt updated successfully!' : 'System prompt created successfully!'));
        // Refresh the prompts list
        fetchSystemPrompts();
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setError(result.message || `Failed to ${isEditMode ? 'update' : 'create'} system prompt`);
      }
    } catch (error) {
      console.error(`${isEditMode ? 'Update' : 'Create'} system prompt error:`, error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setIsEditMode(false);
    setEditingPromptId(null);
    setEditForm({
      categoryName: "",
      prompt: ""
    });
    setError("");
    setSuccess("");
    setFieldErrors({});
  };

  // whether the edit form has no changes compared to original (used to disable Update)
  const isUnchanged = isEditMode && (() => {
    const orig = originalEditForm || { categoryName: "", prompt: "" };
    const origCat = (orig.categoryName || "").trim();
    const newCat = (editForm.categoryName || "").trim();
    const origPrompt = (orig.prompt || "").trim();
    const newPrompt = (editForm.prompt || "").trim();
    return origCat === newCat && origPrompt === newPrompt;
  })();

  const isPromptChanged = () => {
    const current = (editForm.prompt || "").trim();
    const original = (originalEditForm?.prompt || "").trim();
    if (isEditMode) {
      return current !== original;
    }
    return current !== "";
  };

  return (
    <Box sx={{ backgroundColor: "#000000", minHeight: "100%", width: "100%" }}>
      {/* Header */}
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 4,
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 2, md: 0 }
      }}>
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              marginBottom: 1,
              fontSize: { xs: "24px", md: "32px" }
            }}
          >
            Customize Chat AI
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "#888888",
              fontSize: "16px"
            }}
          >
            Manage Chat Integrations & customizations
          </Typography>
        </Box>

        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          onClick={handleOpenModal}
          sx={{
            backgroundColor: "#E1AD01",
            color: "#000000",
            borderRadius: "25px",
            padding: "12px 24px",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#B8941F"
            }
          }}
        >
          Configure Categories
        </Button>
      </Box>

      {/* Customize Chat Section */}
      {/* <Card sx={{ 
        backgroundColor: "#1A1A1A", 
        borderRadius: "16px", 
        marginBottom: 4,
        border: "1px solid #333333"
      }}>
        <CardContent sx={{ padding: 4 }}>
          <Typography
            variant="h6"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              marginBottom: 3,
              fontSize: "20px"
            }}
          >
            Customize Chat
          </Typography>

          <Box sx={{ 
            display: "grid", 
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, 
            gap: 3,
            marginBottom: 3
          }}>
            <Box>
              <CustomInputField
                label="Set Question No."
                placeholder="Enter"
                value={chatForm.questionNo}
                onChange={(e) => handleChatFormChange("questionNo", e.target.value)}
              />
            </Box>

            <Box>
              <CustomInputField
                label="Set coins per question"
                placeholder="Enter"
                value={chatForm.coinsPerQuestion}
                onChange={(e) => handleChatFormChange("coinsPerQuestion", e.target.value)}
              />
            </Box>

            <Box sx={{ gridColumn: { xs: "1", md: "1" } }}>
              <CustomInputField
                label="Set Price per coins"
                placeholder="Set Price"
                value={chatForm.pricePerCoins}
                onChange={(e) => handleChatFormChange("pricePerCoins", e.target.value)}
              />
            </Box>
          </Box>

          <Box sx={{ marginBottom: 3 }}>
            <CustomInputField
              label="Set Generalized Prompt"
              placeholder="Type Here"
              value={chatForm.generalizedPrompt}
              onChange={(e) => handleChatFormChange("generalizedPrompt", e.target.value)}
              multiline={true}
              rows={6}
            />

          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: "#E1AD01",
                color: "#000000",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#B8941F"
                }
              }}
            >
              Save
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Customize Chat Categories Section */}
      <Card sx={{
        backgroundColor: "#1A1A1A",
        borderRadius: "16px",
        marginBottom: 4,
        border: "1px solid #333333"
      }}>
        <CardContent sx={{ padding: 4 }}>

          {/* Add Categories List */}
          <Typography
            variant="h6"
            sx={{
              color: "#FFFFFF",
              fontWeight: 600,
              marginBottom: 3,
              fontSize: "18px"
            }}
          >
            System Prompts
          </Typography>

          <Box sx={{ marginBottom: 4 }}>
            {loadingPrompts ? (
              <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
                <CircularProgress sx={{ color: "#E1AD01" }} />
              </Box>
            ) : systemPrompts.length > 0 ? (
              getCurrentPageItems().map((prompt) => (
                <Box
                  key={prompt._id?.$oid || prompt._id}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    backgroundColor: "#2A2A2A",
                    borderRadius: "12px",
                    padding: "16px 20px",
                    marginBottom: 2,
                    border: "1px solid #333333"
                  }}
                >
                  <Box sx={{ flex: 1, paddingRight: 2 }}>
                    <Typography sx={{
                      color: "#CCCCCC",
                      fontSize: "16px",
                      fontWeight: 500,
                      lineHeight: 1.6,
                    }}>
                      {prompt.category}
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={() => handleEditCategory(prompt._id?.$oid || prompt._id)}
                    sx={{
                      backgroundColor: "#E1AD01",
                      color: "#000000",
                      width: 36,
                      height: 36,
                      "&:hover": {
                        backgroundColor: "#B8941F"
                      }
                    }}
                  >
                    <Edit sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              ))
            ) : (
              <Box sx={{
                textAlign: "center",
                padding: 4,
                color: "#888888"
              }}>
                <Typography>No system prompts found. Add your first prompt above.</Typography>
              </Box>
            )}
          </Box>

          {/* Pagination */}
          {systemPrompts.length > itemsPerPage && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                sx={{
                  borderColor: "#666666",
                  color: "#CCCCCC",
                  borderRadius: "20px",
                  padding: "8px 20px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#E1AD01",
                    backgroundColor: "rgba(212, 175, 55, 0.1)"
                  },
                  "&:disabled": {
                    borderColor: "#333333",
                    color: "#666666"
                  }
                }}
              >
                Previous
              </Button>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                {getPageNumbers(currentPage, totalPages).map((page, idx) =>
                  page === "..." ? (
                    <Box key={`ellipsis-${idx}`} sx={{ color: "#999999", fontSize: "14px", px: 0.5 }}>...</Box>
                  ) : (
                    <Box
                      key={page}
                      onClick={() => handlePageChange(page)}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        backgroundColor: page === currentPage ? "#E1AD01" : "#333333",
                        color: page === currentPage ? "#000000" : "#FFFFFF",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "14px",
                        fontWeight: 600,
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: page === currentPage ? "#B8941F" : "#444444"
                        }
                      }}
                    >
                      {page}
                    </Box>
                  )
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                sx={{
                  backgroundColor: "#E1AD01",
                  color: "#000000",
                  borderRadius: "20px",
                  padding: "8px 20px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#B8941F"
                  },
                  "&:disabled": {
                    backgroundColor: "#333333",
                    color: "#666666"
                  }
                }}
              >
                Next
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Edit Category Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#FFFFFF",
            borderRadius: "20px",
            padding: "8px"
          }
        }}
      >
        <DialogTitle sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "24px 32px 16px 32px",
        }}>
          <Typography sx={{
            color: "#333333",
            fontSize: "24px",
            fontWeight: 600
          }}>
            {isEditMode ? "Edit Category Prompt" : "Customize Chat Categories"}
          </Typography>
          <IconButton
            onClick={handleCloseModal}
            sx={{
              color: "#666666",
              "&:hover": { backgroundColor: "#F0F0F0" }
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 32px 32px 32px", }}>
          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                marginBottom: 2,
                backgroundColor: "#ffebee",
                color: "#c62828",
                whiteSpace: "pre-line"
              }}
              onClose={() => setError("")}
            >
              {error}
            </Alert>
          )}

          {/* Success Alert */}
          {success && (
            <Alert
              severity="success"
              sx={{
                marginBottom: 2,
                backgroundColor: "#e8f5e8",
                color: "#2e7d32"
              }}
            >
              {success}
            </Alert>
          )}

          {/* Neutral Alert for no-changes */}
          {neutralMessage && (
            <Alert
              severity="info"
              sx={{
                marginBottom: 2,
                backgroundColor: "#f0f4ff",
                color: "#0d47a1"
              }}
            >
              {neutralMessage}
            </Alert>
          )}

          <Box sx={{ marginBottom: 3, marginTop: 2 }}>
            <Box sx={{ position: "relative" }}>
              <Typography
                sx={{
                  position: "absolute",
                  top: "-9px",
                  left: "14px",
                  backgroundColor: "rgba(204, 158, 73, 1)",
                  color: "black",
                  padding: "4px 12px",
                  borderRadius: "12px",
                  fontSize: "12px",
                  fontWeight: 500,
                  zIndex: 10
                }}
              >
                Category Name
              </Typography>
              <Autocomplete
                freeSolo
                value={editForm.categoryName}
                onChange={(event, newValue) => {
                  handleEditFormChange("categoryName", newValue || "");
                  if (error) setError("");
                  if (fieldErrors.categoryName) {
                    setFieldErrors(prev => ({ ...prev, categoryName: "" }));
                  }
                }}
                onInputChange={(event, newInputValue) => {
                  handleEditFormChange("categoryName", newInputValue);
                  if (error) setError("");
                  if (fieldErrors.categoryName) {
                    setFieldErrors(prev => ({ ...prev, categoryName: "" }));
                  }
                }}
                disabled={loading}
                options={isEditMode ? [] : CHAT_CATEGORY_OPTIONS.map((option) => option.value)}
                popupIcon={isEditMode ? null : undefined}
                disableClearable={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select or type category"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#FFFFFF",
                        borderRadius: "12px",
                        "& fieldset": {
                          borderColor: "#E1AD01",
                        },
                        "&:hover fieldset": {
                          borderColor: "#E1AD01",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#E1AD01",
                          borderWidth: "2px",
                        },
                        "&.Mui-disabled fieldset": {
                          borderColor: "#E0E0E0",
                        },
                        "& input": {
                          padding: "14px 16px",
                          color: "#000000",
                        },
                        "& input.Mui-disabled": {
                          color: "#000000 !important",
                          WebkitTextFillColor: "#000000 !important"
                        }
                      }
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-popupIndicator": {
                    color: "#E1AD01"
                  },
                  "& .MuiAutocomplete-clearIndicator": {
                    color: "#E1AD01"
                  }
                }}
                ListboxProps={{
                  sx: {
                    backgroundColor: "#FFFFFF",
                    "& .MuiAutocomplete-option": {
                      color: "#333333",
                      fontSize: "14px",
                      padding: "12px 16px",
                      "&:hover": {
                        backgroundColor: "#F5F5F5"
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#F5F5F5"
                      },
                      '&[aria-selected="true"]': {
                        backgroundColor: "#E1AD01",
                        color: "#000000",
                        "&:hover": {
                          backgroundColor: "#B8941F"
                        }
                      }
                    }
                  }
                }}
              />
            </Box>
            {fieldErrors.categoryName && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.categoryName}
              </Typography>
            )}
          </Box>

          <Box sx={{ marginBottom: 4 }}>
              <CustomInputField
              label="Set Category Prompt"
              placeholder="Type Here"
              value={editForm.prompt}
              onChange={(e) => {
                handleEditFormChange("prompt", e.target.value);
                if (error) setError("");
                if (fieldErrors.prompt) {
                  setFieldErrors(prev => ({ ...prev, prompt: "" }));
                }
              }}
              disabled={loading}
              multiline={true}
              rows={6}
              
            />
            {fieldErrors.prompt && (
              <Typography sx={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px", marginLeft: "14px" }}>
                {fieldErrors.prompt}
              </Typography>
            )}

          </Box>

          <Box sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-end"
          }}>
            <Button
              variant="outlined"
              onClick={handleModalReset}
              disabled={loading || (!isEditMode && !isPromptChanged())}
              sx={{
                borderColor: "#E1AD01",
                color: "#E1AD01",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                "&.Mui-disabled": {
                  borderColor: "#E1AD01",
                  color: "#E1AD01",
                  opacity: 0.6,
                  pointerEvents: "none",
                },
                "&:hover": {
                  borderColor: "#B8941F",
                  backgroundColor: "rgba(212, 175, 55, 0.1)"
                }
              }}
            >
              {isEditMode ? "Cancel" : "Reset"}
            </Button>
            <Button
              variant="contained"
              onClick={handleModalCustomize}
              disabled={loading || isUnchanged}
              sx={{
                backgroundColor: "#E1AD01",
                color: "#000000",
                borderRadius: "25px",
                padding: "12px 32px",
                fontWeight: 600,
                textTransform: "none",
                position: "relative",
                "&:hover": {
                  backgroundColor: "#B8941F"
                },
                "&.Mui-disabled": {
                  cursor: "not-allowed",
                  backgroundColor: "#b8941fb3",
                  pointerEvents: "auto",
                  color: "#666666"
                }
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: "#000" }} />
                  {isEditMode ? "Updating..." : "Adding..."}
                </Box>
              ) : (
                isEditMode ? "Update" : "Add"
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ChatAI;